// frontend/src/components/pages/chat/PromptInput.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  Paperclip,
  ArrowUp,
  Wrench,
  Image as ImageIcon,
  Mic,
  Square,
  WandSparkles,
  Search as SearchIcon,
} from "lucide-react";
import TextareaAutosize from "react-textarea-autosize";

import { SquareCode, Globe, X } from "lucide-react";
import FileAttachment from "./FileAttachment";
import { getTextDirection } from "../../../utils/textUtils";
import SpeechRecorder from "./SpeechRecorder";
import { motion, AnimatePresence } from "framer-motion";
import { enhancePrompt } from "../../../api/apiFunction/chatService";
import { toast } from "react-toastify";

const PromptInput = ({
  prompt,
  isChatLoading,
  handleSubmit,
  onPromptChange,
  file,
  onFileChange,
  activeTool,
  setActiveTool,
  stopGenerating,
  subscription,
  selectedModel,
  models,
}) => {
  // activeTool and setActiveTool are now received as props from Chat.jsx
  console.log("models", models);
  console.log("selectedModel", selectedModel);
  const [showToolsDropdown, setShowToolsDropdown] = useState(false);
  const toolsDropdownRef = useRef(null);
  const toolsButtonRef = useRef(null);
  const fileAttachmentRef = useRef();

  // Speech recognition state
  const [isListening, setIsListening] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const stopRecordingRef = useRef(null); // Ref to store the stop recording function

  // Enhanced prompt state
  const [isEnhancing, setIsEnhancing] = useState(false);

  // Check if selected model is Qwen Image Edit (specific model ID)
  const QWEN_IMAGE_EDIT_ID = "6983283779630987cf98ec72";
  const selectedModelData = models?.find(
    (model) => model._id === selectedModel
  );
  const isQwenImageEdit = selectedModel === QWEN_IMAGE_EDIT_ID || selectedModelData?._id === QWEN_IMAGE_EDIT_ID;
  const isInputDisabled = isQwenImageEdit && !file;

  // Timer for speech recording
  useEffect(() => {
    let id;
    if (isListening) {
      id = setInterval(() => setSeconds((s) => s + 1), 1000);
    } else {
      setSeconds(0);
    }
    return () => clearInterval(id);
  }, [isListening]);

  // Format time for display
  const formatTime = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Determine direction for the prompt input
  const promptDirection = getTextDirection(prompt);
  const promptAlign = promptDirection === "rtl" ? "right" : "left";

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        toolsDropdownRef.current &&
        !toolsDropdownRef.current.contains(event.target) &&
        toolsButtonRef.current &&
        !toolsButtonRef.current.contains(event.target)
      ) {
        setShowToolsDropdown(false);
      }
    }
    if (showToolsDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showToolsDropdown]);

  const handleKeyDown = (e) => {
    // Block Enter key submission if subscription is past_due or canceled
    if (
      subscription &&
      (subscription.status === "past_due" || subscription.status === "canceled")
    ) {
      return;
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (prompt.trim()) {
        // Stop recording if it's active when sending the message
        if (isListening && stopRecordingRef.current) {
          stopRecordingRef.current();
        }
        handleSubmit(e, file, activeTool);
      }
    }
  };

  // Remove file after sending (parent will clear file prop)
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Block submission if subscription is past_due or canceled
    if (
      subscription &&
      (subscription.status === "past_due" || subscription.status === "canceled")
    ) {
      return;
    }

    // Stop recording if it's active when sending the message
    if (isListening && stopRecordingRef.current) {
      stopRecordingRef.current();
    }
    await handleSubmit(e, file, activeTool, prompt);
  };

  // Expose file picker from FileAttachment
  const openFilePicker = () => {
    // Clear attached file before opening picker to allow re-uploading the same file
    onFileChange(null);
    if (fileAttachmentRef.current && fileAttachmentRef.current.openFileDialog) {
      fileAttachmentRef.current.openFileDialog();
    }
  };

  // Handle prompt enhancement
  const handleEnhancePrompt = async () => {
    if (!prompt.trim() || isEnhancing) return;

    setIsEnhancing(true);
    try {
      const response = await enhancePrompt(prompt.trim());
      if (response?.data?.enhancedPrompt) {
        // Create synthetic event to match expected format
        const syntheticEvent = {
          target: { value: response.data.enhancedPrompt },
        };
        onPromptChange(syntheticEvent);
        toast.success("Prompt enhanced successfully!");
      } else {
        toast.error("Failed to enhance prompt");
      }
    } catch (error) {
      toast.error("Failed to enhance prompt");
    } finally {
      setIsEnhancing(false);
    }
  };

  return (
    <div className="p-2 md:p-4 bg-transparent">
      <div className="max-w-3xl mx-auto">
        {/* Main Prompt Input Container */}
        <div className="bg-bg-50 rounded-xl md:rounded-2xl relative">
          <form onSubmit={handleFormSubmit} className="p-2 md:p-3 pt-2">
            {/* File Attachment Preview */}
            <div className={file ? "mb-5 md:mb-6" : ""}>
              <FileAttachment
                ref={fileAttachmentRef}
                file={Array.isArray(file) ? null : file} // Single file mode (backward compatibility)
                files={Array.isArray(file) ? file : null} // Multiple files mode
                onFileSelect={onFileChange}
                onRemove={() => onFileChange(null)}
                hideButton={true} // Always hide the attach file button - use paperclip icon in controls instead
              />
            </div>

            {/* Special image upload button for image editing */}
            {(activeTool === "image" || isQwenImageEdit) && !file && (
              <div className="mb-2">
                <button
                  type="button"
                  className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg bg-ac-02 hover:bg-ac-01 text-white text-xs md:text-sm border border-ac-02 transition-colors touch-manipulation"
                  onClick={() => {
                    openFilePicker();
                  }}
                  title="Upload an image to edit"
                >
                  <ImageIcon size={14} />
                  <span className="whitespace-nowrap">Upload Image to Edit</span>
                </button>
                {/* Image editing hint - moved here to avoid overlap */}
                <div className="mt-2 text-[10px] md:text-xs text-fg-60">
                  {isQwenImageEdit
                    ? "This model requires an image upload. Please upload an image to enable editing."
                    : "Upload an image first, then describe your edits..."}
                </div>
              </div>
            )}
            
            {/* Message for Qwen Image Edit model when input is disabled */}
            {isQwenImageEdit && !file && (
              <div className="mb-3 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                <p className="text-sm text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
                  <ImageIcon size={16} />
                  <span>Upload an image to edit. This model does not support image generation.</span>
                </p>
              </div>
            )}

            {/* Text Input Only */}
            <div className={`w-full flex items-center gap-1.5 md:gap-2 relative py-1.5 md:py-2 ${activeTool === "image" && file ? "pt-7 md:pt-8" : ""}`}>
              {/* Image editing hint - only show when file is uploaded */}
              {activeTool === "image" && file && (
                <div className="absolute -top-7 md:-top-8 left-0 text-[10px] md:text-xs text-fg-60 whitespace-nowrap">
                  Describe how you want to edit this image...
                </div>
              )}
              {/* Active Tool Icon (if any) */}
              {activeTool === "code" && (
                <span className="ml-2 mr-2 text-ac-02 opacity-70 flex items-center">
                  <SquareCode size={20} strokeWidth={1.5} />
                </span>
              )}
              {activeTool === "web" && (
                <span className="ml-2 mr-2 text-ac-02 opacity-70 flex items-center">
                  <Globe size={20} strokeWidth={1.5} />
                </span>
              )}
              {activeTool === "image" && (
                <span className="ml-2 mr-2 text-ac-02 opacity-70 flex items-center">
                  <ImageIcon size={20} strokeWidth={1.5} />
                </span>
              )}
              {activeTool === "youtube" && (
                <span className="ml-2 mr-2 text-ac-02 opacity-70 flex items-center">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4c2.9-.8 9.1-.8 9.1-.8s6.2 0 9.1.8a2 2 0 0 1 1.4 1.4 24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4c-2.9.8-9.1.8-9.1.8s-6.2 0-9.1-.8A2 2 0 0 1 2.5 17Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      fill="none"
                    />
                    <path
                      d="m10 15 5-3-5-3v6Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      fill="none"
                    />
                  </svg>
                </span>
              )}
              {activeTool === "research" && (
                <span className="ml-2 mr-2 text-ac-02 opacity-70 flex items-center">
                  <SearchIcon size={20} strokeWidth={1.5} />
                </span>
              )}
              <TextareaAutosize
                value={prompt || ""}
                onChange={onPromptChange}
                disabled={
                  isInputDisabled ||
                  (subscription &&
                    (subscription.status === "past_due" ||
                      subscription.status === "canceled"))
                }
                placeholder={
                  isInputDisabled
                    ? "Upload an image to edit..."
                    : subscription &&
                      (subscription.status === "past_due" ||
                        subscription.status === "canceled")
                      ? subscription.status === "past_due"
                        ? "Your subscription is past due"
                        : "Your subscription is canceled"
                      : activeTool === "youtube"
                        ? "Paste YouTube URL..."
                        : activeTool === "web"
                          ? "Ask a question to search..."
                          : activeTool === "code"
                            ? "Describe the code..."
                            : activeTool === "image"
                              ? "Describe the image..."
                              : activeTool === "research"
                                ? "Ask a research question..."
                                : "Type your message..."
                }
                className={`w-full py-2 md:py-3 px-2 md:px-4 rounded-lg md:rounded-xl bg-transparent placeholder-fg-70 focus:outline-none focus:ring-1 focus:ring-bd-50 resize-none break-words text-sm md:text-base ${
                  isEnhancing ? "shimmer-effect" : ""
                } ${
                  subscription &&
                  (subscription.status === "past_due" ||
                    subscription.status === "canceled")
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
                minRows={1}
                maxRows={5}
                onKeyDown={handleKeyDown}
                style={{
                  paddingLeft: activeTool ? 12 : undefined,
                  direction: promptDirection,
                  textAlign: promptAlign,
                }}
                dir={promptDirection}
              />
            </div>
            {/* Controls Row: Tools, Attach, Model Dropdown (left) and Send Button (right) */}
            <div className="flex items-center gap-1.5 md:gap-2 justify-between relative flex-wrap">
              <div className="flex items-center gap-1.5 md:gap-2 relative">
                {/* Tools Dropdown Button */}
                <button
                  type="button"
                  ref={toolsButtonRef}
                  onClick={() => setShowToolsDropdown((prev) => !prev)}
                  className={`p-2 rounded-lg transition-colors focus:outline-none text-fg-50 touch-manipulation ${
                    showToolsDropdown ? "bg-bg-50" : "hover:bg-bg-40"
                  }`}
                  title="Tools"
                >
                  <Wrench size={18} strokeWidth={1.5} />
                </button>
                {/* Tools Dropdown (opens upwards) */}
                {showToolsDropdown && (
                  <div
                    ref={toolsDropdownRef}
                    className="absolute left-0 bottom-full mb-2 z-50 bg-bg-60 rounded-2xl shadow-xl border border-bd-50 min-w-[200px] overflow-hidden animate-fade-in-up"
                  >
                    <div className="px-4 py-3 bg-bg-50">
                      <h3 className="text-xs font-medium text-fg-50">Tools</h3>
                    </div>
                    <button
                      type="button"
                      className={`w-full flex items-center justify-between px-4 py-3 text-left text-fg-50 transition-colors text-sm ${
                        activeTool === "code"
                          ? "bg-bg-50 font-medium"
                          : "hover:bg-bg-50"
                      }`}
                      onClick={() => {
                        if (activeTool === "code") {
                          setActiveTool(null);
                        } else {
                          setActiveTool("code");
                        }
                        setShowToolsDropdown(false);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <SquareCode
                          size={16}
                          strokeWidth={1.5}
                          className={
                            activeTool === "code" ? "text-ac-02" : "text-fg-60"
                          }
                        />
                        <span className="text-xs font-medium text-fg-50">
                          Write Code
                        </span>
                      </div>
                      {activeTool === "code" && (
                        <span
                          className="inline-flex items-center justify-center text-fg-60 hover:text-red-500 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveTool(null);
                            setShowToolsDropdown(false);
                          }}
                          title="Deselect tool"
                        >
                          <X size={14} />
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`w-full flex items-center justify-between px-4 py-3 text-left text-fg-50 transition-colors text-sm ${
                        activeTool === "web"
                          ? "bg-bg-50 font-medium"
                          : "hover:bg-bg-50"
                      }`}
                      onClick={() => {
                        if (activeTool === "web") {
                          setActiveTool(null);
                        } else {
                          setActiveTool("web");
                        }
                        setShowToolsDropdown(false);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <Globe
                          size={16}
                          strokeWidth={1.5}
                          className={
                            activeTool === "web" ? "text-ac-02" : "text-fg-60"
                          }
                        />
                        <span className="text-xs font-medium text-fg-50">
                          Search Web
                        </span>
                      </div>
                      {activeTool === "web" && (
                        <span
                          className="inline-flex items-center justify-center text-fg-60 hover:text-red-500 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveTool(null);
                            setShowToolsDropdown(false);
                          }}
                          title="Deselect tool"
                        >
                          <X size={14} />
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`w-full flex items-center justify-between px-4 py-3 text-left text-fg-50 transition-colors text-sm ${
                        activeTool === "image"
                          ? "bg-bg-50 font-medium"
                          : "hover:bg-bg-50"
                      }`}
                      onClick={() => {
                        if (activeTool === "image") {
                          setActiveTool(null);
                        } else {
                          setActiveTool("image");
                        }
                        setShowToolsDropdown(false);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <ImageIcon
                          size={16}
                          strokeWidth={1.5}
                          className={
                            activeTool === "image" ? "text-ac-02" : "text-fg-60"
                          }
                        />
                        <span className="text-xs font-medium text-fg-50">
                          Generate Image
                        </span>
                      </div>
                      {activeTool === "image" && (
                        <span
                          className="inline-flex items-center justify-center text-fg-60 hover:text-red-500 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveTool(null);
                            setShowToolsDropdown(false);
                          }}
                          title="Deselect tool"
                        >
                          <X size={14} />
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`w-full flex items-center justify-between px-4 py-3 text-left text-fg-50 transition-colors text-sm ${
                        activeTool === "youtube"
                          ? "bg-bg-50 font-medium"
                          : "hover:bg-bg-50"
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (activeTool === "youtube") {
                          setActiveTool(null);
                        } else {
                          setActiveTool("youtube");
                        }
                        setShowToolsDropdown(false);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className={
                            activeTool === "youtube"
                              ? "text-ac-02"
                              : "text-fg-60"
                          }
                        >
                          <path
                            d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4c2.9-.8 9.1-.8 9.1-.8s6.2 0 9.1.8a2 2 0 0 1 1.4 1.4 24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4c-2.9-.8-9.1-.8-9.1-.8s-6.2 0-9.1-.8A2 2 0 0 1 2.5 17Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            fill="none"
                          />
                          <path
                            d="m10 15 5-3-5-3v6Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            fill="none"
                          />
                        </svg>
                        <span className="text-xs font-medium text-fg-50">
                          YouTube Recap
                        </span>
                      </div>
                      {activeTool === "youtube" && (
                        <span
                          className="inline-flex items-center justify-center text-fg-60 hover:text-red-500 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveTool(null);
                            setShowToolsDropdown(false);
                          }}
                          title="Deselect tool"
                        >
                          <X size={14} />
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`w-full flex items-center justify-between px-4 py-3 text-left text-fg-50 transition-colors text-sm ${
                        activeTool === "research"
                          ? "bg-bg-50 font-medium"
                          : "hover:bg-bg-50"
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (activeTool === "research") {
                          setActiveTool(null);
                        } else {
                          setActiveTool("research");
                        }
                        setShowToolsDropdown(false);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <SearchIcon
                          size={16}
                          strokeWidth={1.5}
                          className={
                            activeTool === "research"
                              ? "text-ac-02"
                              : "text-fg-60"
                          }
                        />
                        <span className="text-xs font-medium text-fg-50">
                          Deep Research
                        </span>
                      </div>
                      {activeTool === "research" && (
                        <span
                          className="inline-flex items-center justify-center text-fg-60 hover:text-red-500 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveTool(null);
                            setShowToolsDropdown(false);
                          }}
                          title="Deselect tool"
                        >
                          <X size={14} />
                        </span>
                      )}
                    </button>
                  </div>
                )}
                {/* File Attach (Paperclip) Button */}
                <button
                  type="button"
                  className="p-2 rounded-lg text-fg-50 hover:bg-bg-40 transition-colors touch-manipulation"
                  title="Attach file"
                  onClick={openFilePicker}
                >
                  <Paperclip size={18} strokeWidth={1.5} />
                </button>

                {/* Enhanced Prompt (Magic) Button - Only show when there's text */}
                <AnimatePresence>
                  {prompt.trim() && (
                    <motion.button
                      type="button"
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.85 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      className={`p-2 opacity-65 rounded-lg transition-colors ${
                        isEnhancing
                          ? "text-ac-02 bg-bg-50"
                          : "text-fg-50 hover:bg-bg-50"
                      }`}
                      title="Enhance prompt with AI"
                      onClick={handleEnhancePrompt}
                      disabled={isEnhancing}
                    >
                      <motion.div
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.85 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <WandSparkles
                          strokeWidth={1.5}
                          size={18}
                          className={isEnhancing ? "animate-spin" : ""}
                        />
                      </motion.div>
                    </motion.button>
                  )}
                </AnimatePresence>

                {/* Search Tool Button */}
                <button
                  type="button"
                  className={`flex items-center gap-1.5 md:gap-2 px-2 md:px-3 pr-3 md:pr-4 py-1.5 md:py-2 rounded-full transition-colors touch-manipulation ${
                    activeTool === "web"
                      ? "bg-ac-02 text-white dark:text-black"
                      : "text-fg-50 hover:bg-bg-40"
                  }`}
                  title="Search web"
                  onClick={() => {
                    if (activeTool === "web") {
                      setActiveTool(null);
                    } else {
                      setActiveTool("web");
                    }
                  }}
                >
                  <Globe
                    size={14}
                    strokeWidth={1.5}
                    className="md:w-4 md:h-4"
                  />
                  <span className="text-xs md:text-sm">Search</span>
                </button>

                {/* Image Tool Button */}
                <button
                  type="button"
                  className={`flex items-center gap-1.5 md:gap-2 px-2 md:px-3 pr-3 md:pr-4 py-1.5 md:py-2 rounded-full transition-colors touch-manipulation ${
                    activeTool === "image"
                      ? "bg-ac-02 text-white dark:text-black"
                      : "text-fg-50 hover:bg-bg-40"
                  }`}
                  title="Generate image"
                  onClick={() => {
                    if (activeTool === "image") {
                      setActiveTool(null);
                    } else {
                      setActiveTool("image");
                    }
                  }}
                >
                  <ImageIcon
                    size={14}
                    strokeWidth={1.5}
                    className="md:w-4 md:h-4"
                  />
                  <span className="text-xs md:text-sm">Image</span>
                </button>

                {/* Research Tool Button */}
                <button
                  type="button"
                  className={`flex items-center gap-1.5 md:gap-2 px-2 md:px-3 pr-3 md:pr-4 py-1.5 md:py-2 rounded-full transition-colors touch-manipulation ${
                    activeTool === "research"
                      ? "bg-ac-02 text-white dark:text-black"
                      : "text-fg-50 hover:bg-bg-40"
                  }`}
                  title="Deep research"
                  onClick={() => {
                    if (activeTool === "research") {
                      setActiveTool(null);
                    } else {
                      setActiveTool("research");
                    }
                  }}
                >
                  <SearchIcon
                    size={14}
                    strokeWidth={1.5}
                    className="md:w-4 md:h-4"
                  />
                  <span className="text-xs md:text-sm">Research</span>
                </button>
              </div>

              {/* Right side: Timer + Speech Recorder + Send Button */}
              <div className="flex items-center gap-1.5 md:gap-2">
                {/* Speech Recorder Container with Hover Expansion */}
                <div className="relative group">
                  <SpeechRecorder
                    value={prompt}
                    onTranscriptChange={(text) => {
                      // Create synthetic event to match expected format
                      const syntheticEvent = {
                        target: { value: text },
                      };
                      onPromptChange(syntheticEvent);
                    }}
                    onListeningChange={setIsListening}
                    render={({
                      isListening,
                      toggle,
                      selectedLanguage,
                      setSelectedLanguage,
                      availableLanguages,
                      showLanguageMenu,
                      setShowLanguageMenu,
                      getCurrentLanguageInfo,
                    }) => {
                      // Store the toggle function in ref so we can stop recording when message is sent
                      stopRecordingRef.current = isListening ? toggle : null;
                      const currentLangInfo = getCurrentLanguageInfo();

                      return (
                        <div
                          className={`flex items-center rounded-lg transition-all duration-300 ease-out ${
                            isListening || showLanguageMenu
                              ? "bg-bg-40 px-2 py-1"
                              : "group-hover:bg-bg-40 group-hover:px-2 group-hover:py-1"
                          }`}
                        >
                          {/* Animation bars positioned to the left of timer */}
                          <AnimatePresence>
                            {isListening && (
                              <motion.div
                                className="flex items-center gap-0.5 mr-1"
                                initial={{ opacity: 0, width: 0 }}
                                animate={{ opacity: 1, width: "auto" }}
                                exit={{ opacity: 0, width: 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                {[...Array(4)].map((_, i) => (
                                  <motion.div
                                    key={i}
                                    className="w-0.5 bg-fg-50 rounded-full"
                                    animate={{
                                      height: isListening ? [3, 12, 3] : 3,
                                    }}
                                    transition={{
                                      duration: isListening ? 0.8 : 0.3,
                                      repeat: isListening ? Infinity : 0,
                                      delay: isListening ? i * 0.1 : 0,
                                      ease: "easeInOut",
                                    }}
                                  />
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>

                          <AnimatePresence>
                            {isListening && (
                              <motion.span
                                className="text-xs text-fg-60 select-none w-10 text-right tabular-nums"
                                initial={{ opacity: 0, width: 0 }}
                                animate={{ opacity: 1, width: "40px" }}
                                exit={{ opacity: 0, width: 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                {formatTime(seconds)}
                              </motion.span>
                            )}
                          </AnimatePresence>

                          {/* Language selector - show on hover or when dropdown is open */}
                          {!isListening && (
                            <div
                              className={`relative ml-1 transition-all duration-300 ${
                                showLanguageMenu
                                  ? "opacity-100"
                                  : "opacity-0 group-hover:opacity-100"
                              }`}
                            >
                              <button
                                ref={(el) => {
                                  // Create a ref that can be accessed by SpeechRecorder's click outside handler
                                  if (el) {
                                    el.setAttribute(
                                      "data-language-button",
                                      "true",
                                    );
                                  }
                                }}
                                type="button"
                                className="flex items-center gap-0.5 p-1 rounded text-fg-50 hover:bg-bg-50 transition-colors text-xs"
                                title={`Speech language: ${currentLangInfo.name}`}
                                onClick={() =>
                                  setShowLanguageMenu(!showLanguageMenu)
                                }
                                disabled={isChatLoading}
                              >
                                <span className="text-sm">
                                  {currentLangInfo.flag}
                                </span>
                                <svg
                                  width="10"
                                  height="10"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <polyline points="6,9 12,15 18,9"></polyline>
                                </svg>
                              </button>

                              {/* Language dropdown */}
                              {showLanguageMenu && (
                                <div
                                  ref={(el) => {
                                    // Create a ref that can be accessed by SpeechRecorder's click outside handler
                                    if (el) {
                                      el.setAttribute(
                                        "data-language-dropdown",
                                        "true",
                                      );
                                    }
                                  }}
                                  className="absolute bottom-full right-0 mb-2 bg-bg-60 rounded-lg shadow-xl border border-bd-50 min-w-[140px] z-50"
                                >
                                  <div className="px-3 py-2 bg-bg-50 text-xs font-medium text-fg-50 border-b border-bd-50">
                                    Language
                                  </div>
                                  {availableLanguages.map((lang) => (
                                    <button
                                      key={lang.code}
                                      type="button"
                                      className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${
                                        selectedLanguage === lang.code
                                          ? "bg-bg-50 text-ac-02"
                                          : "text-fg-50 hover:bg-bg-50"
                                      }`}
                                      onClick={() => {
                                        setSelectedLanguage(lang.code);
                                        setShowLanguageMenu(false);
                                      }}
                                    >
                                      <span className="text-base">
                                        {lang.flag}
                                      </span>
                                      <span className="text-xs">
                                        {lang.name}
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          <button
                            type="button"
                            className={`flex items-center justify-center size-9 md:size-10 rounded-full text-fg-50 hover:bg-bg-40 transition-colors touch-manipulation ${
                              isListening
                                ? "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400"
                                : ""
                            }`}
                            title={
                              isListening
                                ? "Stop recording"
                                : `Record audio (${currentLangInfo.name})`
                            }
                            onClick={toggle}
                            aria-pressed={isListening}
                            disabled={isChatLoading}
                          >
                            {isListening ? (
                              <Square
                                size={16}
                                fill="currentColor"
                                strokeWidth={0}
                                className="md:w-4 md:h-4"
                              />
                            ) : (
                              <Mic
                                size={16}
                                strokeWidth={1.5}
                                className="md:w-4 md:h-4"
                              />
                            )}
                          </button>
                        </div>
                      );
                    }}
                  />
                </div>

                {/* Send/Stop Button */}
                {isChatLoading ? (
                  <button
                    type="button"
                    className="flex items-center justify-center size-9 md:size-10 rounded-full bg-ac-02 text-white dark:text-black hover:bg-ac-01 touch-manipulation"
                    onClick={stopGenerating}
                    title="Stop generating"
                    aria-label="Stop generating"
                  >
                    {/* Use a stop icon, e.g., a square or X */}
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="md:w-5 md:h-5"
                    >
                      <rect
                        x="5"
                        y="5"
                        width="10"
                        height="10"
                        rx="2"
                        fill="currentColor"
                      />
                    </svg>
                  </button>
                ) : (
                  (() => {
                    // Check if subscription is past_due or canceled
                    const isSubscriptionBlocked =
                      subscription &&
                      (subscription.status === "past_due" ||
                        subscription.status === "canceled");

                    const isDisabled =
                      !prompt.trim() || isChatLoading || isSubscriptionBlocked;

                    // Get tooltip message
                    const getTooltipMessage = () => {
                      if (isSubscriptionBlocked) {
                        return subscription.status === "past_due"
                          ? "Your subscription is past due"
                          : "Your subscription is canceled";
                      }
                      if (!prompt.trim()) {
                        return "Enter a message to send";
                      }
                      if (isChatLoading) {
                        return "Please wait...";
                      }
                      return "Send message";
                    };

                    return (
                      <div className="relative group">
                        <button
                          type="submit"
                          disabled={isDisabled}
                          className={`flex items-center justify-center size-9 md:size-10 rounded-full transition-colors touch-manipulation ${
                            isDisabled
                              ? "bg-bg-40 text-fg-60 cursor-not-allowed"
                              : "bg-ac-02 text-white dark:text-black hover:bg-ac-01"
                          }`}
                          title={getTooltipMessage()}
                        >
                          <ArrowUp size={18} className="md:w-5 md:h-5" />
                        </button>
                        {/* Tooltip for disabled state */}
                        {isSubscriptionBlocked && (
                          <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-bg-60 dark:bg-bg-50 text-fg-50 text-sm rounded-lg shadow-lg border border-bd-50 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                            {subscription.status === "past_due"
                              ? "Your subscription is past due"
                              : "Your subscription is canceled"}
                            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-bg-60 dark:border-t-bg-50"></div>
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default React.memo(PromptInput);
